import { pageTitle } from '@palaccount/config';
import {
  TASK_KINDS,
  TASK_PRIORITIES,
  TASK_RECURRENCES,
  TASK_RELATED_KINDS,
  type TaskKind,
  type TaskPriority,
  type TaskRecurrence,
  type TaskRelatedKind,
  type TaskResponse,
  type TaskStatus,
} from '@palaccount/contracts';
import { Bell, Check, Repeat, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { Modal } from '@/features/inventory/modal';
import { usePagedState, usePageRange } from '@/lib/paging';
import { localizeServerCause } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import {
  useCancelTask,
  useCompleteTask,
  useCreateTask,
  useFollowUpTask,
  useReopenTask,
  useTaskActivity,
  useTaskCalendar,
  useTasks,
  useTaskStats,
  type TaskListFilters,
} from './api';
import { CrmTabs } from './crm-tabs';
import { dueText } from './task-format';

/**
 * CRM-03 — Follow-ups: the ONE task engine's screen. My Tasks (four urgency
 * groups, empty ones omitted), Team (scope named by the SERVER's
 * task-visibility answer — BR-CRM-03-13), a month Calendar, and the Overdue
 * view. Priority, recurrence and reminder are ALWAYS-VISIBLE inputs whose
 * defaults the server supplies (BR-CRM-03-02, owner ruling R1). Arriving
 * with ?new=1&relatedKind=…&relatedId=… opens the form pre-filled
 * (BR-CRM-03-15 — every Add task button routes here).
 */

const KIND_META: Record<TaskKind, { key: string; chip: string }> = {
  call: { key: 'crm.tkCall', chip: 'bg-blue-100 text-blue-800' },
  email: { key: 'crm.tkEmail', chip: 'bg-violet-100 text-violet-800' },
  meeting: { key: 'crm.tkMeeting', chip: 'bg-amber-100 text-amber-800' },
  todo: { key: 'crm.tkTodo', chip: 'bg-muted text-muted-foreground' },
  demo: { key: 'crm.tkDemo', chip: 'bg-emerald-100 text-emerald-800' },
};

const RELATED_KEYS: Record<TaskRelatedKind, string> = {
  lead: 'crm.rkLead',
  opportunity: 'crm.rkOpportunity',
  customer: 'crm.rkCustomer',
  project: 'crm.rkProject',
  // HR-05: interviews are tasks related to a candidate (BR-HR-05-08).
  candidate: 'crm.rkCandidate',
};

const PRIORITY_KEYS: Record<TaskPriority, string> = {
  low: 'crm.prLow',
  normal: 'crm.prNormal',
  high: 'crm.prHigh',
};

const RECURRENCE_KEYS: Record<TaskRecurrence, string> = {
  none: 'crm.rcNone',
  daily: 'crm.rcDaily',
  weekly: 'crm.rcWeekly',
  monthly: 'crm.rcMonthly',
};

type View = 'mine' | 'team' | 'calendar' | 'overdue';

/** Every list on this screen asks for one block at a time (NFR-42). */
const TASK_PAGE_SIZE = 25;

/**
 * ONE of §5's four urgency groups: its own paged read of the same route,
 * carrying the DUE WINDOW `/stats` counted against.
 *
 * A component rather than a `.map()` body because each group is its own query
 * and its own page, and a hook cannot be called inside a loop — the shape
 * CRM-01's `BoardColumn` and CRM-02's `StageColumn` both take.
 *
 * The heading's number is the SERVER's count for the window, not the length
 * of the block below it, so "This week 40" over twenty-five rows is a page
 * rather than a contradiction.
 */
function UrgencyGroup({
  name,
  titleKey,
  alert,
  count,
  filters,
  renderRow,
}: {
  name: string;
  titleKey: string;
  alert: boolean;
  count: number;
  filters: TaskListFilters;
  renderRow: (task: TaskResponse) => React.ReactNode;
}) {
  const { t } = useTranslation();
  const paging = usePagedState(TASK_PAGE_SIZE, name);
  const list = useTasks(filters, paging.page, TASK_PAGE_SIZE);
  const range = usePageRange(list.data, TASK_PAGE_SIZE);

  return (
    <Card className={cn(alert && 'border-red-300')}>
      <CardContent className="grid gap-2">
        <div className="flex items-center justify-between">
          <h4 className={cn('text-sm font-bold', alert && 'text-red-700 dark:text-red-400')}>{t(titleKey)}</h4>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 font-mono text-xs',
              alert ? 'bg-red-100 text-red-700' : 'bg-muted text-muted-foreground',
            )}
          >
            {count}
          </span>
        </div>
        {(list.data?.tasks ?? []).map(renderRow)}
        {range.pages > 1 ? (
          <Pager
            page={paging.page}
            pages={range.pages}
            first={range.first}
            last={range.last}
            total={range.total}
            onPage={paging.setPage}
            label={t(titleKey)}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

export function FollowupsPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState<View>('mine');
  const [formOpen, setFormOpen] = useState(params.get('new') === '1');
  const [error, setError] = useState('');
  const [completing, setCompleting] = useState<TaskResponse | null>(null);
  const [teamStatus, setTeamStatus] = useState<'' | TaskStatus | 'overdue'>('');

  useEffect(() => {
    document.title = pageTitle(t('crm.followupsTitle'));
  }, [t]);

  // The server answers with the scope it actually applied (BR-CRM-03-13).
  const wide = view === 'team' || view === 'calendar' || view === 'overdue';
  const askedScope = wide ? ('all' as const) : undefined;
  /**
   * The badges and the four group counts, over EVERY task (NFR-42, §3 step
   * 10). It also carries the WINDOW BOUNDARIES it counted against, so each
   * group's own paged read asks for exactly what was counted — and, unlike
   * the `setHours(0,0,0,0)` this replaced, those boundaries are the COMPANY's
   * midnight rather than the reader's machine's.
   */
  const { data: stats } = useTaskStats(askedScope);
  const scope = stats?.scope ?? 'mine';

  const complete = useCompleteTask();
  const reopen = useReopenTask();
  const cancel = useCancelTask();
  const followUp = useFollowUpTask();

  const act = (promise: Promise<unknown>) => {
    setError('');
    promise.catch((cause: unknown) => {
      // The cause reaches the decision — a refusal keeps the server's sentence
      // localized, a request that never answered gets this screen's own rather
      // than the browser's `Failed to fetch`. The key is the follow-ups
      // screen's alone: leads and opportunities share the `crm.` namespace but
      // are different screens and may need different sentences.
      setError(localizeServerCause(t, cause, 'crm.errSaveFailedFollowup'));
    });
  };

  const openCount = stats?.open ?? 0;
  const overdueCount = stats?.overdue ?? 0;

  /**
   * §5's four urgency groups, as four WINDOWS rather than four `.filter()`
   * calls. Each becomes its own paged read below (BR-CRM-03-12 still decides
   * that an empty group is not drawn — the count says whether it is empty).
   *
   * The boundaries come from `/stats`, so the heading's number and the block
   * under it are answers to the same question.
   */
  const groups = useMemo(() => {
    if (stats === undefined) return [];
    return [
      {
        key: 'overdue' as const,
        titleKey: 'crm.gOverdue',
        alert: true,
        count: stats.buckets.overdue,
        filters: { overdue: true },
      },
      {
        key: 'today' as const,
        titleKey: 'crm.gToday',
        alert: false,
        count: stats.buckets.today,
        filters: { status: 'pending' as const, dueFrom: stats.window.now, dueTo: stats.window.todayEnd },
      },
      {
        key: 'week' as const,
        titleKey: 'crm.gWeek',
        alert: false,
        count: stats.buckets.week,
        filters: {
          status: 'pending' as const,
          dueFrom: stats.window.todayEnd,
          dueTo: stats.window.weekEnd,
        },
      },
      {
        key: 'upcoming' as const,
        titleKey: 'crm.gUpcoming',
        alert: false,
        count: stats.buckets.later,
        filters: { status: 'pending' as const, dueFrom: stats.window.weekEnd },
      },
    ].filter((group) => group.count > 0);
  }, [stats]);

  /**
   * The Team view's chip is a SERVER filter now. `overdue` is the derived one
   * — `TASK_OVERDUE` beside the fold it mirrors — and it is why this could
   * not simply gain a LIMIT before the filters moved.
   */
  const teamPaging = usePagedState(TASK_PAGE_SIZE, 'team');
  const teamQuery = useMemo(
    () => ({
      scope: 'all' as const,
      ...(teamStatus === 'overdue' ? { overdue: true } : {}),
      ...(teamStatus !== '' && teamStatus !== 'overdue' ? { status: teamStatus } : {}),
    }),
    [teamStatus],
  );
  const teamList = useTasks(teamQuery, teamPaging.page, TASK_PAGE_SIZE);
  const teamFiltered = teamList.data?.tasks ?? [];
  const teamRange = usePageRange(teamList.data, TASK_PAGE_SIZE);

  /** The Overdue view — the same predicate, its own page. */
  const overduePaging = usePagedState(TASK_PAGE_SIZE, 'overdue');
  const overdueList = useTasks(
    { scope: 'all', overdue: true },
    overduePaging.page,
    TASK_PAGE_SIZE,
  );
  const overdueTasks = overdueList.data?.tasks ?? [];
  const overdueRange = usePageRange(overdueList.data, TASK_PAGE_SIZE);

  const tick = (task: TaskResponse) => {
    if (task.status === 'cancelled') return; // inert (BR-CRM-03-05)
    if (task.status === 'completed') {
      act(reopen.mutateAsync(task.id));
      return;
    }
    setCompleting(task); // the completion prompt — skippable (BR-CRM-03-09)
  };

  const row = (task: TaskResponse) => (
    <div key={task.id} className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm">
      <button
        type="button"
        aria-label={t('crm.tickTask', { title: task.title })}
        onClick={() => tick(task)}
        className={cn(
          'flex size-5 items-center justify-center rounded-full border',
          task.status === 'completed' && 'border-emerald-700 bg-emerald-700 text-white',
          task.status === 'cancelled' && 'opacity-40',
        )}
      >
        {task.status === 'completed' ? <Check className="size-3" aria-hidden /> : null}
      </button>
      {/* BR-CRM-03-16: BOTH terminal states strike the title through — a
          cancelled task is as finished as a completed one, and the rule names
          the two together. `tasks.status` is pinned to exactly three values
          (`CHECK (status IN ('pending', 'completed', 'cancelled'))`), so this
          pair is the complete non-pending set and not a sample of it. The tick
          above distinguishes them (filled for completed, faded for cancelled);
          the strike-through does not, deliberately. */}
      <span
        className={cn(
          'me-auto font-medium',
          (task.status === 'completed' || task.status === 'cancelled') &&
            'text-muted-foreground line-through',
        )}
      >
        {task.title}
      </span>
      <span className={cn('rounded-full px-2 py-0.5 text-xs', KIND_META[task.kind].chip)}>
        {t(KIND_META[task.kind].key)}
      </span>
      {task.relatedKey ? (
        <Ltr value={task.relatedKey} className="font-mono text-xs text-muted-foreground" />
      ) : null}
      {task.priority !== 'normal' ? (
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-semibold',
            task.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-muted text-muted-foreground',
          )}
        >
          {t(PRIORITY_KEYS[task.priority])}
        </span>
      ) : null}
      {task.recurrence !== 'none' ? (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Repeat className="size-3" aria-hidden /> {t(RECURRENCE_KEYS[task.recurrence])}
        </span>
      ) : null}
      {task.reminder ? (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Bell className="size-3" aria-hidden />
          {t('crm.minBefore', { count: task.reminderMinutes })}
        </span>
      ) : null}
      {task.assigneeName ? (
        <span className="text-xs text-muted-foreground">{task.assigneeName}</span>
      ) : null}
      <span className={cn('text-xs', task.overdue ? 'font-bold text-red-600 dark:text-red-400' : 'text-muted-foreground')}>
        <Ltr value={dueText(t, task.dueAt)} className="font-mono" />
      </span>
      {task.status === 'pending' ? (
        <span className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => act(followUp.mutateAsync(task.id))}>
            {t('crm.addFollowUp')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            aria-label={t('crm.cancelTask', { title: task.title })}
            className="text-red-600 dark:text-red-400"
            onClick={() => act(cancel.mutateAsync(task.id))}
          >
            <X className="size-3" aria-hidden />
          </Button>
        </span>
      ) : null}
    </div>
  );

  return (
    <div className="grid gap-4">
      <CrmTabs />
      <div className="flex flex-wrap items-center gap-3">
        <div className="me-auto">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            {t('crm.eyebrow')}
          </div>
          <h2 className="text-lg font-bold">{t('crm.followupsTitle')}</h2>
        </div>
        <Button onClick={() => setFormOpen((value) => !value)}>{t('crm.newTask')}</Button>
      </div>

      {error ? (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-1">
        {(
          [
            ['mine', 'crm.vMyTasks'],
            ['team', 'crm.vTeam'],
            ['calendar', 'crm.vCalendar'],
          ] as const
        ).map(([name, key]) => (
          <Button
            key={name}
            size="sm"
            variant={view === name ? 'default' : 'outline'}
            onClick={() => setView(name)}
          >
            {t(key)}
          </Button>
        ))}
        <Button
          size="sm"
          variant={view === 'overdue' ? 'destructive' : 'outline'}
          className={cn(view !== 'overdue' && 'border-red-300 text-red-600 dark:text-red-400')}
          onClick={() => setView('overdue')}
        >
          {/* BR-CRM-03-13: the tab names its own scope. */}
          {t(scope === 'all' ? 'crm.vAllOverdue' : 'crm.vMyOverdue', { count: overdueCount })}
        </Button>
      </div>

      {formOpen ? (
        <NewTaskForm
          prefill={{
            relatedKind: (params.get('relatedKind') as TaskRelatedKind | null) ?? undefined,
            relatedId: params.get('relatedId') ?? undefined,
            relatedKey: params.get('relatedKey') ?? undefined,
            relatedName: params.get('relatedName') ?? undefined,
          }}
          onClose={() => {
            setFormOpen(false);
            setParams({}, { replace: true });
          }}
        />
      ) : null}

      {view === 'mine' ? (
        <div className="grid gap-3">
          <h3 className="text-sm font-bold text-muted-foreground">
            {t('crm.myTasksOpen', { count: openCount })}
          </h3>
          {groups.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('crm.allCaughtUp')}</p>
          ) : (
            groups.map((group) => (
              <UrgencyGroup
                key={group.key}
                name={group.key}
                titleKey={group.titleKey}
                alert={group.alert}
                count={group.count}
                filters={group.filters}
                renderRow={row}
              />
            ))
          )}
        </div>
      ) : null}

      {view === 'team' ? (
        <>
        <Card>
          <CardContent className="grid gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <NativeSelect
                aria-label={t('crm.filterStatus')}
                className="w-auto"
                value={teamStatus}
                onChange={(event) =>
                  teamPaging.change(setTeamStatus)(event.target.value as typeof teamStatus)
                }
              >
                <option value="">{t('crm.allStatuses')}</option>
                <option value="pending">{t('crm.tsPending')}</option>
                <option value="overdue">{t('crm.tsOverdue')}</option>
                <option value="completed">{t('crm.tsCompleted')}</option>
                <option value="cancelled">{t('crm.tsCancelled')}</option>
              </NativeSelect>
              <span className="ms-auto text-xs text-muted-foreground">
                {t(scope === 'all' ? 'crm.scopeAll' : 'crm.scopeMine')}
              </span>
            </div>
            {teamFiltered.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('crm.noTasksMatch')}</p>
            ) : (
              teamFiltered.map(row)
            )}
            {(teamList.data?.total ?? 0) > 0 ? (
              <Pager
                page={teamPaging.page}
                pages={teamRange.pages}
                first={teamRange.first}
                last={teamRange.last}
                total={teamRange.total}
                onPage={teamPaging.setPage}
                label={t('crm.vTeam')}
              />
            ) : null}
          </CardContent>
        </Card>
        {/* §7: "The activity report, under the team list". */}
        <ActivityReport />
        </>
      ) : null}

      {view === 'calendar' ? <MonthCalendar /> : null}

      {view === 'overdue' ? (
        <Card className="border-red-300">
          <CardContent className="grid gap-2">
            <h4 className="text-sm font-bold text-red-700 dark:text-red-400">
              {t(scope === 'all' ? 'crm.vAllOverdue' : 'crm.vMyOverdue', {
                count: overdueCount,
              })}
            </h4>
            {overdueTasks.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('crm.nothingOverdue')}</p>
            ) : (
              overdueTasks.map(row)
            )}
            {(overdueList.data?.total ?? 0) > 0 ? (
              <Pager
                page={overduePaging.page}
                pages={overdueRange.pages}
                first={overdueRange.first}
                last={overdueRange.last}
                total={overdueRange.total}
                onPage={overduePaging.setPage}
                label={t('crm.vMyOverdue', { count: overdueCount })}
              />
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* The completion prompt — skippable; completing is what spawns the
          next copy of a repeating task, not the prompt (BR-CRM-03-07/09). */}
      {completing ? (
        <CompletionPrompt
          task={completing}
          onSave={(outcome) =>
            act(
              complete
                .mutateAsync({ taskId: completing.id, ...(outcome ? { outcome } : {}) })
                .then(() => setCompleting(null)),
            )
          }
          onSkip={() =>
            act(complete.mutateAsync({ taskId: completing.id }).then(() => setCompleting(null)))
          }
          onFollowUp={(outcome) =>
            act(
              complete
                .mutateAsync({ taskId: completing.id, ...(outcome ? { outcome } : {}) })
                .then(() => followUp.mutateAsync(completing.id))
                .then(() => setCompleting(null)),
            )
          }
          onClose={() => setCompleting(null)}
        />
      ) : null}
    </div>
  );
}

/** 5d — the New task form; Create disabled until title + due (BR-CRM-03-01). */
function NewTaskForm({
  prefill,
  onClose,
}: {
  prefill: {
    relatedKind?: TaskRelatedKind | undefined;
    relatedId?: string | undefined;
    relatedKey?: string | undefined;
    relatedName?: string | undefined;
  };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateTask();
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<TaskKind>('todo');
  const [relatedKind, setRelatedKind] = useState<'' | TaskRelatedKind>(prefill.relatedKind ?? '');
  const [relatedName, setRelatedName] = useState(prefill.relatedName ?? '');
  const [dueAt, setDueAt] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [recurrence, setRecurrence] = useState<TaskRecurrence>('none');
  const [reminder, setReminder] = useState(false);
  const [reminderMinutes, setReminderMinutes] = useState('15');
  const [error, setError] = useState('');

  const complete = title.trim().length > 0 && dueAt !== '';

  return (
    <Card className="border-primary">
      <CardContent className="grid gap-3">
        <div className="flex items-center gap-2">
          <h3 className="me-auto text-base font-bold">{t('crm.newTaskTitle')}</h3>
          <button type="button" aria-label={t('common.close')} onClick={onClose}>
            <X className="size-4" aria-hidden />
          </button>
        </div>
        {error ? (
          <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
          <div className="grid gap-1">
            <Label htmlFor="task-title">{t('crm.fTitle')}</Label>
            <Input id="task-title" value={title} onChange={(event) => setTitle(event.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="task-kind">{t('crm.fType')}</Label>
            <NativeSelect
              id="task-kind"
              value={kind}
              onChange={(event) => setKind(event.target.value as TaskKind)}
            >
              {TASK_KINDS.map((value) => (
                <option key={value} value={value}>
                  {t(KIND_META[value].key)}
                </option>
              ))}
            </NativeSelect>
          </div>
        </div>
        <div className="grid gap-3 border-t border-dashed pt-3 sm:grid-cols-3">
          <div className="grid gap-1">
            <Label htmlFor="task-related-kind">{t('crm.fRelatedTo')}</Label>
            <NativeSelect
              id="task-related-kind"
              value={relatedKind}
              onChange={(event) => setRelatedKind(event.target.value as '' | TaskRelatedKind)}
            >
              <option value="">—</option>
              {TASK_RELATED_KINDS.map((value) => (
                <option key={value} value={value}>
                  {t(RELATED_KEYS[value])}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1">
            <Label htmlFor="task-record">{t('crm.fRecord')}</Label>
            <Input
              id="task-record"
              placeholder={t('crm.fRecordPh')}
              value={relatedName}
              onChange={(event) => setRelatedName(event.target.value)}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="task-due">{t('crm.fDue')}</Label>
            <Input
              id="task-due"
              type="datetime-local"
              dir="ltr"
              value={dueAt}
              onChange={(event) => setDueAt(event.target.value)}
            />
          </div>
        </div>
        {/* Always-visible priority / recurrence / reminder (BR-CRM-03-02). */}
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="grid gap-1">
            <Label htmlFor="task-priority">{t('crm.fPriority')}</Label>
            <NativeSelect
              id="task-priority"
              value={priority}
              onChange={(event) => setPriority(event.target.value as TaskPriority)}
            >
              {TASK_PRIORITIES.map((value) => (
                <option key={value} value={value}>
                  {t(PRIORITY_KEYS[value])}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1">
            <Label htmlFor="task-recurrence">{t('crm.fRecurrence')}</Label>
            <NativeSelect
              id="task-recurrence"
              value={recurrence}
              onChange={(event) => setRecurrence(event.target.value as TaskRecurrence)}
            >
              {TASK_RECURRENCES.map((value) => (
                <option key={value} value={value}>
                  {t(RECURRENCE_KEYS[value])}
                </option>
              ))}
            </NativeSelect>
          </div>
          {/*
            ═══ ERR-CRM-03-07 — THE ROW THIS STRIP NOW ANSWERS ══════════════

            The row (spec line 23012) reads: "Set a reminder and expected a
            notification | The hint says (mock) — nothing is actually sent |
            Treat reminders as data to build against". It documents the
            PROTOTYPE, and until now only its first half was answered here:
            reminder and lead minutes are stored fields with server defaults
            (BR-CRM-03-02) and the row bell renders them.

            THE SECOND HALF IS NOW BUILT, so the hint this strip carries is a
            promise the product keeps: `notification-sweep.ts` raises a
            `task_reminder` to the assignee at the due time minus the lead
            time, which is the spec's own answer in the CRM-03 open-questions
            table (Q-CRM-03-2, spec line 23062).

            THE EARLIER ARGUMENT HERE WAS NOT WRONG, IT WAS WAITING. This
            comment previously carried the scanner's ignore mark and declined
            to cite the row, on the ground that "NOTIFICATION_KINDS carries no
            task kind today" and that a hint claiming a notification would
            claim a path that did not exist. That was correct while sending
            was fake. The kind exists now, so the condition it named has been
            met and the mark comes off with it — the two readings are a
            sequence, not a disagreement.

            The hint is the spec's §5d row 4 sentence (line 22836) with the
            "(mock)" parenthetical dropped, which is what the defect row asks
            for. Nothing in this build ever rendered the word "(mock)".
          */}
          <div className="grid gap-1">
            <Label htmlFor="task-reminder-toggle">{t('crm.fReminder')}</Label>
            <div className="flex items-center gap-2 rounded-md border px-2 py-1.5">
              <Bell className="size-4 text-muted-foreground" aria-hidden />
              <input
                id="task-reminder-toggle"
                type="checkbox"
                checked={reminder}
                onChange={(event) => setReminder(event.target.checked)}
              />
              <Input
                aria-label={t('crm.fReminderLead')}
                className="h-7 w-16 font-mono"
                dir="ltr"
                inputMode="numeric"
                value={reminderMinutes}
                onChange={(event) => setReminderMinutes(event.target.value.replace(/\D/g, ''))}
              />
              <span className="text-xs text-muted-foreground">{t('crm.minBeforeLabel')}</span>
            </div>
            <p className="text-xs text-muted-foreground">{t('crm.reminderHint')}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!complete || create.isPending}
            onClick={() =>
              create
                .mutateAsync({
                  title: title.trim(),
                  dueAt: new Date(dueAt).toISOString(),
                  kind,
                  ...(relatedKind ? { relatedKind } : {}),
                  ...(prefill.relatedId ? { relatedId: prefill.relatedId } : {}),
                  ...(prefill.relatedKey ? { relatedKey: prefill.relatedKey } : {}),
                  ...(relatedName.trim() ? { relatedName: relatedName.trim() } : {}),
                  ...(priority !== 'normal' ? { priority } : {}),
                  ...(recurrence !== 'none' ? { recurrence } : {}),
                  ...(reminder ? { reminder, reminderMinutes: Number(reminderMinutes) || 15 } : {}),
                })
                .then(onClose)
                .catch((cause: unknown) => {
                  setError(localizeServerCause(t, cause, 'crm.errSaveFailedFollowup'));
                })
            }
          >
            {t('crm.createTask')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CompletionPrompt({
  task,
  onSave,
  onSkip,
  onFollowUp,
  onClose,
}: {
  task: TaskResponse;
  onSave: (outcome: string) => void;
  onSkip: () => void;
  onFollowUp: (outcome: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState('');
  return (
    <Modal title={t('crm.taskCompleted')} onClose={onClose}>
      <div className="grid gap-3">
        <p className="text-sm text-muted-foreground">{task.title}</p>
        {task.recurrence !== 'none' ? (
          <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            <Repeat className="size-3" aria-hidden /> {t('crm.nextOccurrence')}
          </div>
        ) : null}
        <div className="grid gap-1">
          <Label htmlFor="task-outcome">{t('crm.outcomeLabel')}</Label>
          <Textarea
            id="task-outcome"
            placeholder={t('crm.outcomePh')}
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
          />
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onSkip}>
            {t('crm.skip')}
          </Button>
          <Button variant="outline" onClick={() => onFollowUp(outcome.trim())}>
            {t('crm.scheduleFollowUp')}
          </Button>
          <Button onClick={() => onSave(outcome.trim())}>{t('common.save')}</Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * §7's Activity report, under the Team list — BR-CRM-03-18: "must count
 * against the completion time, not the due date, and must drop users with
 * nothing completed."
 *
 * Both demands are the SERVER's (see `tasks.service.activity`): the window
 * filters on `completed_at`, and the GROUP BY yields rows only for users who
 * completed something — so this component draws every row it receives and
 * never filters, which is what keeps the rule one implementation rather than
 * two. The heading, subtitle and the two large numbers are §7's; the caption
 * prints the week the server counted (Sat–Fri, company clock — the boundary
 * and its open question live in `activityWeek`, not here). Scope follows the
 * Team list's own convention: asked as 'all', resolved by the server
 * (BR-CRM-03-13), so the report describes the same population as the list
 * above it.
 */
function ActivityReport() {
  const { t } = useTranslation();
  const activity = useTaskActivity('all');
  const report = activity.data;
  if (!report) return null;
  const maxTasks = Math.max(1, ...report.byUser.map((row) => row.tasks));
  return (
    <Card>
      <CardContent className="grid gap-3">
        <div className="flex flex-wrap items-baseline gap-2">
          <h4 className="text-sm font-bold">{t('crm.activityTitle')}</h4>
          <span className="text-xs text-muted-foreground">{t('crm.activitySubtitle')}</span>
          <Ltr
            value={`${report.weekStart} – ${report.weekEnd}`}
            className="ms-auto font-mono text-xs text-muted-foreground"
          />
        </div>
        <div className="flex flex-wrap gap-6">
          <div>
            <div className="text-2xl font-bold tabular-nums">{report.callsCompleted}</div>
            <div className="text-xs text-muted-foreground">{t('crm.activityCalls')}</div>
          </div>
          <div>
            <div className="text-2xl font-bold tabular-nums">{report.tasksCompleted}</div>
            <div className="text-xs text-muted-foreground">{t('crm.activityTasks')}</div>
          </div>
        </div>
        {report.byUser.length === 0 ? (
          /* BR-DSH-00-20's convention: its own empty sentence, not a blank. */
          <p className="text-sm text-muted-foreground">{t('crm.activityEmpty')}</p>
        ) : (
          <div className="grid gap-1.5">
            {report.byUser.map((row) => (
              <div key={row.userId} className="grid grid-cols-[10rem_1fr_auto] items-center gap-2">
                <span className="truncate text-sm">{row.userName}</span>
                <div className="h-2 rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-primary"
                    style={{ width: `${(row.tasks / maxTasks) * 100}%` }}
                  />
                </div>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {row.tasks}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** The month calendar: 42 cells, at most 3 chips + "+n more" (BR-CRM-03-17). */
function MonthCalendar() {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState(() => new Date());

  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const cells = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
  const today = new Date();
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  /*
   * NFR-42 — the grid asks for ONE ROW PER DAY over its own six-week window,
   * never for the month's tasks. A cell prints three titles and a count, so
   * that is what the route returns; thirty-one-ish rows are bounded by the
   * WINDOW rather than by the tenant, which is PAGINATION.md §5d's argument
   * and the reason this is an aggregate rather than a fifth paged list.
   */
  const end = new Date(start);
  end.setDate(start.getDate() + 42);
  const { data: calendar } = useTaskCalendar(start.toISOString(), end.toISOString(), 'all');
  const byDay = useMemo(
    () => new Map((calendar?.days ?? []).map((entry) => [entry.day, entry])),
    [calendar],
  );
  /** The cell's own key, in the same `YYYY-MM-DD` the server bucketed on. */
  const dayKey = (day: Date) =>
    `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;

  const dayNames = [0, 1, 2, 3, 4, 5, 6].map((index) => t(`crm.day${index}`));

  return (
    <Card>
      <CardContent className="grid gap-2">
        <div className="flex items-center gap-2">
          <h4 className="me-auto text-sm font-bold">
            {t(`crm.month${anchor.getMonth()}`)} <span dir="ltr">{anchor.getFullYear()}</span>
          </h4>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))}
          >
            {t('crm.prev')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAnchor(new Date())}>
            {t('crm.today')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))}
          >
            {t('crm.next')}
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-px text-center text-[11px] font-semibold text-muted-foreground">
          {dayNames.map((name) => (
            <div key={name}>{name}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px rounded-md border bg-border">
          {cells.map((day) => {
            const inMonth = day.getMonth() === anchor.getMonth();
            const ofDay = byDay.get(dayKey(day));
            return (
              <div
                key={day.toISOString()}
                className={cn('min-h-16 bg-background p-1', !inMonth && 'opacity-40')}
              >
                <div
                  className={cn(
                    'mb-1 inline-flex size-5 items-center justify-center rounded-full font-mono text-[11px]',
                    sameDay(day, today) && 'bg-primary font-bold text-primary-foreground',
                  )}
                >
                  {day.getDate()}
                </div>
                {(ofDay?.titles ?? []).map((title, index) => (
                  <div
                    // The day plus the position: two tasks in one square may
                    // share a title, and the aggregate carries no ids.
                    key={`${dayKey(day)}-${index}`}
                    className="mb-0.5 truncate rounded bg-muted px-1 text-[10px]"
                  >
                    {title}
                  </div>
                ))}
                {(ofDay?.count ?? 0) > 3 ? (
                  <div className="text-[10px] text-muted-foreground">
                    {t('crm.moreChips', { count: ofDay!.count - 3 })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
